import { memo, useCallback, useEffect, useRef, useState } from "react";
import { NodeProps, useReactFlow } from "reactflow";
import { NodeResizer } from "@reactflow/node-resizer";
import { TextNodeData } from '../../types';
// calculateNodeSize might need adjustment or become less relevant with rich text
// import { calculateNodeSize } from "../../utils/nodeUtils"; 
import "@reactflow/node-resizer/dist/style.css";
import LexicalEditorComponent from "./LexicalEditor";
import { EditorState } from "lexical";


// Function to create a default empty Lexical state
const createEmptyLexicalState = (): string => {
  return JSON.stringify({
    root: {
      children: [
        {
          children: [],
          direction: null,
          format: '',
          indent: 0,
          type: 'paragraph',
          version: 1,
        },
      ],
      direction: null,
      format: '',
      indent: 0,
      type: 'root',
      version: 1,
    },
  });
};

// Function to convert plain text to Lexical JSON state (basic paragraph)
// This is a simplified version. For full fidelity, a proper HTML/Markdown to Lexical conversion is needed.
const plainTextToLexicalState = (text: string): string => {
  if (!text || text.trim() === "") return createEmptyLexicalState();
  // For simplicity, we'll wrap the whole text in a single paragraph.
  // A more robust solution would parse newlines into separate paragraphs.
  return JSON.stringify({
    root: {
      children: [
        {
          children: [{ detail: 0, format: 0, mode: 'normal', style: '', text: text, type: 'text', version: 1 }],
          direction: 'ltr', // Or null
          format: '',
          indent: 0,
          type: 'paragraph',
          version: 1,
        },
      ],
      direction: 'ltr', // Or null
      format: '',
      indent: 0,
      type: 'root',
      version: 1,
    },
  });
};


function TextNode({
  data,
  selected,
  id,
}: NodeProps<TextNodeData>) {
  const nodeRef = useRef<HTMLDivElement>(null);
  const { getNode, setNodes } = useReactFlow();

  const [isEditing, setIsEditing] = useState(false);
  
  const node = getNode(id);
  const [nodeSize, setNodeSize] = useState({ 
    width: (node?.width && node.width > 0) ? node.width : 160, 
    height: (node?.height && node.height > 0) ? node.height : 60 // Increased default height for editor
  });

  // initialContentRef will store the *last saved* Lexical JSON string.
  // It's initialized from data.content and updated by handleContentChange and exitEditMode.
  const initialContentRef = useRef<string>(
    data.content && data.content.startsWith('{"root":') 
      ? data.content 
      : data.content 
        ? plainTextToLexicalState(data.content) 
        : createEmptyLexicalState()
  );
  
  // Ensure data.content is also in Lexical format initially if it wasn't
  // This is important if the node was created with plain text and never edited
  useEffect(() => {
    if (data.content !== initialContentRef.current) {
      // This implies initialContentRef was converted/defaulted
      // and data.content needs to be synced up if onUpdate isn't immediately available
      // or if this is the first render.
      // This effect should ideally run only once or if data.content fundamentally changes externally.
      // For now, we assume initialContentRef is the source of truth for the editor.
      // The main saving logic is in handleContentChange and exitEditMode.
    }
  }, []); // Run once on mount to establish baseline


  const updateNodeContent = useCallback((newContent: string) => {
    // Prevent feedback loop if content hasn't actually changed from the last saved state
    if (newContent === initialContentRef.current && newContent === data.content) return;

    initialContentRef.current = newContent; // Update ref to the latest saved content

    if (data.onUpdate) {
      data.onUpdate({ content: newContent });
    } else {
      setNodes((nds) =>
        nds.map((n) => {
          if (n.id === id) {
            return { ...n, data: { ...n.data, content: newContent } };
          }
          return n;
        })
      );
    }
  }, [id, setNodes, data, data.content]); // data.content in dep array for safety, though initialContentRef is primary comparator

  const handleContentChange = useCallback((editorState: EditorState) => {
    const newContent = JSON.stringify(editorState.toJSON());
    // This onChange is for live updates while typing.
    // The final save on blur is handled by exitEditMode.
    // We still update the node data here for real-time feedback if needed by other parts,
    // but initialContentRef is the key for preventing loops and for blur saving.
    if (newContent !== initialContentRef.current) { // Compare with ref, not data.content directly
      updateNodeContent(newContent);
    }
  }, [updateNodeContent]);


  const onResize = useCallback((_: any, newSize: { width: number; height: number }) => {
    setNodeSize(newSize);
    setNodes(nodes => 
      nodes.map(n => {
        if (n.id === id) {
          return { ...n, width: newSize.width, height: newSize.height };
        }
        return n;
      })
    );
  }, [id, setNodes]);

  const enterEditMode = useCallback(() => {
    if (!isEditing) {
      setIsEditing(true);
    }
  }, [isEditing]);

  const exitEditMode = useCallback((latestEditorState?: string) => {
    if (isEditing) {
      if (latestEditorState && latestEditorState !== initialContentRef.current) {
        updateNodeContent(latestEditorState);
      }
      // If latestEditorState is not provided (e.g. Escape key),
      // we assume handleContentChange has kept initialContentRef.current up-to-date.
      // So, the content in initialContentRef.current is what we consider "saved".
      setIsEditing(false);
    }
  }, [isEditing, updateNodeContent]);

  const handleNodeClick = useCallback(() => {
    // If the node is already selected and not in editing mode, enter editing mode.
    // Otherwise, React Flow will handle the selection on the first click.
    if (selected && !isEditing) {
      enterEditMode();
    }
  }, [selected, isEditing, enterEditMode]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isEditing) {
        // When escaping, we don't have direct access to the Lexical editor's latest state here.
        // We rely on OnChangePlugin having updated initialContentRef.current via handleContentChange.
        // So, we call exitEditMode without an argument.
        exitEditMode(); 
      }
    };
    if (isEditing) {
      document.addEventListener('keydown', handleKeyDown);
    }
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isEditing, exitEditMode]);

  // Prevent React Flow dragging when in edit mode
  useEffect(() => {
    if (nodeRef.current) {
      if (isEditing) {
        nodeRef.current.setAttribute('draggable', 'false');
      } else {
        nodeRef.current.setAttribute('draggable', 'true');
      }
    }
  }, [isEditing]);

  // Auto-resizing based on content is complex with rich text and Lexical.
  // For now, rely on manual NodeResizer.
  // Automatic height adjustment could be a future enhancement.

  return (
    <div
      ref={nodeRef}
      style={{ width: nodeSize.width, height: nodeSize.height }}
      className={`
        text-node-wrapper relative
        text-gray-900 dark:text-gray-100
        ${selected && !isEditing ? "ring-2 ring-blue-500" : ""}
        ${isEditing ? "ring-2 ring-blue-600 shadow-lg" : ""}
        ${!isEditing ? "non-editable-text-node" : ""}
      `}
      onClick={handleNodeClick} // Enter edit mode if selected and not editing; React Flow handles first-click selection
      onDoubleClick={enterEditMode} // Also allow double click to ensure entry
    >
      {selected && !isEditing && ( // Show resizer only when selected and NOT editing
        <NodeResizer
          minWidth={80} 
          minHeight={40}
          maxWidth={800}
          maxHeight={600}
          isVisible={selected && !isEditing}
          onResize={onResize}
          handleStyle={{
            width: '8px',
            height: '8px',
            backgroundColor: '#3b82f6', // blue-500
            border: '1px solid white',
            borderRadius: '2px', // Square-ish handles
          }}
          lineStyle={{
            borderColor: '#3b82f6',
            borderWidth: '1px',
          }}
        />
      )}
      
      <div
        className={`lexical-editor-container w-full h-full ${isEditing ? 'nodrag' : ''}`}
        style={{ cursor: isEditing ? 'text' : 'pointer' }}
        onMouseDown={(e) => {
          if (isEditing) {
            e.stopPropagation();
          }
        }}
      >
        {data.contentVisibility !== "hidden" && (
          <LexicalEditorComponent
            initialEditorState={initialContentRef.current || createEmptyLexicalState()}
            onChange={handleContentChange}
            isEditing={isEditing}
            onBlur={exitEditMode}
            onRequestExitEditing={exitEditMode} // Add this line
          />
        )}
      </div>
      {/* Floating toolbar will be added here, likely within LexicalEditorComponent or portal from it */}
    </div>
  );
}

export default memo(TextNode);
